// src/sidebar/Nav.js
import React, { useEffect, useRef, useContext } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { Link, useLocation } from 'react-router-dom';
import { SidebarData } from './SidebarData';
import { SiMarkdown } from "react-icons/si";
import { AuthContext } from "../../context/AuthContext";

const Nav = ({ sidebar, toggleSidebar }) => {
  const { isAdmin } = useContext(AuthContext);
  const sidebarRef = useRef(null);
  const location = useLocation(); // to highlight active link

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        toggleSidebar();
      }
    };

    if (sidebar) {
      document.addEventListener('mousedown', handleClickOutside);
      // Optional: close on ESC key
      const handleEsc = (e) => {
        if (e.key === 'Escape') toggleSidebar();
      };
      window.addEventListener('keydown', handleEsc);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('keydown', handleEsc);
      };
    }
  }, [sidebar, toggleSidebar]);

  const handleLinkClick = () => toggleSidebar();

  return (
    <>
      {/* Backdrop */}
      {sidebar && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <nav
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-screen w-72 bg-white/95 backdrop-blur-md shadow-2xl 
          border-r border-gray-200/70 transform transition-all duration-300 ease-out z-50
          ${sidebar ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          {/* Header - Logo + Close */}
          <div className="flex items-center justify-between px-5 py-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-sm">
                <SiMarkdown size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                MdZaar
              </h2>
            </div>

            {/* Close button - rounded, hover scale + ring */}
            <button
              onClick={toggleSidebar}
              className="p-2.5 rounded-full text-gray-500 hover:text-gray-900 
                hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-2 
                focus:ring-indigo-400 focus:ring-offset-2 transition-all duration-200"
              aria-label="Close sidebar"
            >
              <AiOutlineClose className="text-xl" />
            </button>
          </div>

          {/* Navigation Links */}
          <ul className="flex-1 overflow-y-auto px-3 py-6 space-y-1">
            {SidebarData
              .filter((item) => {
                // Hide admin route for non-admin users
                if (item.path === "/admin" && !isAdmin) {
                  return false;
                }
                return true;
              })
              .map((item, index) => {
                const isActive = location.pathname === item.path;

                return (
                  <li key={index}>
                    <Link
                      to={item.path}
                      onClick={handleLinkClick}
                      className={`group flex items-center px-4 py-3.5 rounded-xl font-medium transition-all duration-200
            ${isActive
                          ? 'bg-indigo-50 text-indigo-700 shadow-sm'
                          : 'text-gray-700 hover:bg-indigo-50/70 hover:text-indigo-600 hover:shadow-sm'
                        } focus:outline-none focus:ring-2 focus:ring-indigo-400/50 focus:ring-offset-1`}
                    >
                      <span className={`mr-4 text-xl transition-transform duration-200 group-hover:scale-110
            ${isActive ? 'text-indigo-600' : 'text-gray-500 group-hover:text-indigo-500'}`}>
                        {item.icon}
                      </span>

                      <span>{item.title}</span>

                      {isActive && (
                        <span className="ml-auto w-2 h-2 rounded-full bg-indigo-500" />
                      )}
                    </Link>
                  </li>
                );
              })}
          </ul>

          {/* Footer */}
          <div className="px-6 py-5 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-gray-500 text-center">
              © {new Date().getFullYear()} MdZaar • Markdown Editor
            </p>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Nav;